import "./companies-overview.scss";

import { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../../features/helpers";
import { fetchCompaniesOverview, getCompaniesOverviewList } from "../../../store/OrganizationSlice";
import { Building2, ArrowDownCircle, ShoppingCart, Coins } from "lucide-react";
import {
    PageCard, SectionTitle, KpiCard, DataList, DataListColumn,
    StatusBadge, MoneyValue,
} from "../../../components/ui";
import { formatCurrencyBRL, formatUSDT } from "../../../utils/format";

interface CompanyRow {
    id: number;
    name: string;
    isActive: boolean;
    depositsTotal: string;
    purchasesTotal: string;
    usdtTransacted: string;
}

const CompaniesOverview = () => {

    const dispatch = useAppDispatch();
    const companies = useAppSelector(getCompaniesOverviewList) as CompanyRow[] | null;

    useEffect(() => {
        dispatch(fetchCompaniesOverview());
    }, [dispatch]);

    const loading = companies === null;
    const rows = companies ?? [];

    // Totais consolidados (todas as empresas) para os KPIs do topo.
    const totals = useMemo(() => rows.reduce(
        (acc, c) => ({
            deposits:  acc.deposits  + Number(c.depositsTotal  ?? 0),
            purchases: acc.purchases + Number(c.purchasesTotal ?? 0),
            usdt:      acc.usdt      + Number(c.usdtTransacted  ?? 0),
        }),
        { deposits: 0, purchases: 0, usdt: 0 }
    ), [rows]);

    const columns: DataListColumn<CompanyRow>[] = [
        { key: "name",      header: "Empresa",             render: (c) => <strong>{c.name}</strong> },
        { key: "status",    header: "Status",              render: (c) => <StatusBadge status={c.isActive ? "Ativa" : "Inativa"} /> },
        { key: "deposits",  header: "Depositos",           render: (c) => <MoneyValue amount={c.depositsTotal} tone="neutral" />,  align: "right" },
        { key: "purchases", header: "Compras",             render: (c) => <MoneyValue amount={c.purchasesTotal} tone="neutral" />, align: "right" },
        { key: "usdt",      header: "USDT transacionado",  render: (c) => `${formatUSDT(Number(c.usdtTransacted ?? 0))} USDT`,      align: "right", mono: true },
    ];

    return (
        <div className="companies-overview">

            <SectionTitle icon={Building2}>Resumo por Empresa</SectionTitle>

            <section className="kpi-grid kpi-grid-3">
                <KpiCard variant="success" icon={ArrowDownCircle} label="Depositos (total)"    value={formatCurrencyBRL(totals.deposits)} />
                <KpiCard variant="info"    icon={ShoppingCart}    label="Compras (total)"      value={formatCurrencyBRL(totals.purchases)} />
                <KpiCard variant="warn"    icon={Coins}           label="USDT transacionado"   value={`${formatUSDT(totals.usdt)} USDT`} />
            </section>

            <PageCard>
                <SectionTitle icon={Building2} count={rows.length}>Empresas</SectionTitle>
                <DataList
                    columns={columns}
                    rows={rows}
                    loading={loading}
                    empty="Nenhuma empresa encontrada."
                />
            </PageCard>

        </div>
    );
};

export default CompaniesOverview;
